import {
	Component, Output, OnInit, OnChanges, SimpleChanges, OnDestroy,
	EventEmitter,
    Input
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl } from '@angular/forms';
import { Observable, Subject, Subscription } from 'rxjs/Rx';
import { MessageService } from 'base';
import { ConfigMonitorComponentState, MonitorService } from 'monitor';
import { IpAddress, Device } from 'DiscoveryEngine.TS/Core';
import {
    CommandScript,
	CommandScriptKeys,
	MonitorPolicy, MonitorSettingsEntry, MonitorSettings_Config, MonitorSettings_ConfigEntry
} from 'DiscoveryEngine.TS/Monitors';
import { List, ValueEncoder, Guid, ArrayUtils, ObjUtils } from 'DiscoveryEngine.TS/Utils';
import { MonitorConfigComponentBase } from '../MonitorConfigComponentBase';
import { IdentityService } from 'identity';
import { NavigationService, NetFacadeService } from 'netview';
import { DiscResultsService } from 'discovery';
import {
	GetDeviceResultDto, GetMonitorStateResultDto, MonitorStateDto, DisplayDeviceListEntryDto
} from 'UvExplorerServer.Dtos.TS/Session';
import { MonitorDeviceSelectorResult } from '../monitor-device-selector/monitor-device-selector.component';
import { MonitorEntry } from 'DiscoveryEngine.TS/InfoSets/MonitorTable';
import { ProtocolSettings, ProtocolSettingsTypes, SshSettings } from '../../../DiscoveryEngine.TS/Protocols';
import { NetViewDto } from 'UvExplorerServer.Dtos.TS/NetViews';

export class ConfigMonitorListEntry {
	public deviceGuid: string;
	public settingsEntryGuid: string;
	public deviceName: string;
	public deviceDescription: string;
	public ipAddress: string;
	public configName: string;
	public state: string;
	public stateDetails: string;
	public style: string;

	public deviceTag: Device;
	public get hasDeviceTag(): boolean {
		return !!this.deviceTag;
	}

	public ipAddressTag: IpAddress;
	public get hasIpAddressTag(): boolean {
		return !!this.ipAddressTag;
	}

	public settingsEntryTag: MonitorSettings_ConfigEntry;
	public get hasSettingsEntryTag(): boolean {
		return !!this.settingsEntryTag;
	}

	constructor(deviceGuid?: string, device?: Device, settingsEntry?: MonitorSettings_ConfigEntry,
		settingsEntryState?: string, settingsEntryStateDetails?: string) {
		if (deviceGuid) {
			this.deviceGuid = deviceGuid;

			if (settingsEntry) {
				this.settingsEntryGuid = settingsEntry.Guid.ToString();
			}

			if (device) {
				this.deviceName = device.DisplayName;
				this.deviceDescription = device.DisplayDescription;
				this.ipAddress = (device.HasIpAddress ? this.ipAddress = device.IpAddress.ToString() : "");
				this.ipAddressTag = device.IpAddress;
			}
			else {
				this.deviceName = deviceGuid;
				this.deviceDescription = "";
				this.ipAddress = "";
				this.ipAddressTag = null;
			}

			if (settingsEntry) {
				if (settingsEntry.HasName) {
					this.configName = settingsEntry.Name;
				}
			}

			if (settingsEntryState) {
				this.state = settingsEntryState;
				if (this.state === "NoData") {
					this.state = "No Data";
				}

				this.style = "monitor-state-" + settingsEntryState.toLowerCase();
			}
			else {
				this.state = "No Data";
				this.style = "monitor-state-nodata";
			}

			if (settingsEntryStateDetails) {
				this.stateDetails = settingsEntryStateDetails;
			}
			else {
				this.stateDetails = "";
			}

			this.deviceTag = device;
			this.settingsEntryTag = settingsEntry;
		}
		else {
			this.deviceGuid = "";
			this.settingsEntryGuid = "";
			this.deviceName = "";
			this.deviceDescription = "";
			this.ipAddress = "";
			this.configName = "";
			this.state = "No Data";
			this.style = "monitor-state-nodata";
			this.stateDetails = "";

			this.deviceTag = null;
			this.ipAddressTag = null;
			this.settingsEntryTag = null;
		}
	}
}

@Component({
	selector: 'config-devices',
	templateUrl: './configdevices.component.html',
	styleUrls: ['./configdevices.component.scss']
})
export class ConfigDevicesComponent extends MonitorConfigComponentBase implements OnInit, OnChanges, OnDestroy {

	@Input()
	public Network: NetViewDto;

	@Input()
	public monEditorState: ConfigMonitorComponentState;

	public entries: ConfigMonitorListEntry[];
	public deviceSelectorVisible: boolean;

	private deviceCache: Object;
	private stateCache: Object;

	public DeviceCredentials: UntypedFormControl;
	private _ProtocolSettings: ProtocolSettings[];

	public UseHostnames: UntypedFormControl;
	public UseAllIpAddresses: UntypedFormControl;

	private settingsChangeSub: Subscription;
	private locationChangedSub: Subscription;

	constructor(private monService: MonitorService,
		private messageService: MessageService,
		private identityService: IdentityService,
		private navService: NavigationService,
		private discResultsService: DiscResultsService,
		private netFacadeService: NetFacadeService,
		private fb: UntypedFormBuilder) {

		super();

		this.Network = null;

		this.entries = [];
		this.deviceSelectorVisible = false;

		this.deviceCache = {};
		this.stateCache = {};

		this.UseHostnames = null;
		this.UseAllIpAddresses = null;

		this.settingsChangeSub = null;

		this.getCurrentNetwork();
		this.locationChangedSub = this.navService.onLocationChanged.subscribe(() => {
			this.getCurrentNetwork();
		});
	}

	public ngOnInit(): void {
		this.BuildForm();
	}

	public ngOnChanges(changes: SimpleChanges): void {

		this.unsubscribeAllEvents();

		if (this.monEditorState) {
			this.settingsChangeSub = this.monEditorState.settingsChange.subscribe(
				(result: boolean) => {
					if (result) {
						this.syncConfigEntries();
						this.rebuildEntries();
					}
				}
			);
		}

		if (this.visible) {
			this.entries = [];
			this.retrieveDevicesAndMonitorStates();
			this.BuildForm();
		}
	}

	public ngOnDestroy(): void {
		this.unsubscribeAllEvents();
	}

	private unsubscribeAllEvents(): void {
		if (this.settingsChangeSub) {
			this.settingsChangeSub.unsubscribe();
			this.settingsChangeSub = null;
		}
	}

	private retrieveDevicesAndMonitorStates(): void {

		if (this.editor) {
			if (this.editor.Policy.DeviceGuids.Count > 0) {
				let deviceGuidStrs = new List<string>();
				this.editor.Policy.DeviceGuids.ForEach((guid: Guid) => {
					let guidStr = ValueEncoder.EncodeGuid(guid);
					deviceGuidStrs.Add(guidStr);
				});

				this.getDevices(deviceGuidStrs).subscribe(
					() => {
						this.getMonitorState(this.editor.Policy).subscribe(
							() => {
								this.rebuildEntries();
							},
							() => {
								this.rebuildEntries();
								this.messageService.displayErrorMessage("Could not retrieve monitor state info");
							});
					},
					() => {
						this.messageService.displayErrorMessage("Could not retrieve monitor device info");
					});
			}
			else {
				this.rebuildEntries();
			}
		}
	}

	private rebuildEntries(): void {

		this.entries = [];

		if (this.editor) {
			let monPolicy: MonitorPolicy = this.editor.Policy;
			monPolicy.DeviceGuids.ForEach((deviceGuid: Guid) => {
				let deviceGuidStr = ValueEncoder.EncodeGuid(deviceGuid);

				let device: Device = null;
				if (this.deviceCache[deviceGuidStr]) {
					device = this.deviceCache[deviceGuidStr];

					let monEntry: MonitorEntry = monPolicy.DeviceMonitorMap.GetMonitor_DeviceGuid(deviceGuid);
					if (!!monEntry && monEntry.Settings.Count > 0) {
						monEntry.Settings.GetEntries().ForEach((monSetsEntry: MonitorSettingsEntry) => {
							let monSetsEntryGuidStr = ValueEncoder.EncodeGuid(monSetsEntry.Guid);

							let state: string = "";
							let stateDetails: string = "";

							let key = this.stateKey(monSetsEntryGuidStr, deviceGuidStr);
							if (this.stateCache[key]) {
								let monState: MonitorStateDto = this.stateCache[key];
								state = monState.monitorSettingsEntryState;
								stateDetails = monState.monitorSettingsEntryStateDetails;
							}
							else {
								console.error("Could not find monitor entry state in cache: " + key);
							}

							let monSetsConfigEntry = <MonitorSettings_ConfigEntry>(monSetsEntry);

							let entry = new ConfigMonitorListEntry(deviceGuidStr, device, monSetsConfigEntry,
								state, stateDetails);
							this.entries.push(entry);
						});
					}
					else {
						let entry = new ConfigMonitorListEntry(deviceGuidStr, device, null, null, null);
						this.entries.push(entry);
					}
				}
				else {
					console.error("Could not find device in cache: " + deviceGuidStr);
				}
			});
		}
	}

	private getDevices(deviceGuids: List<string>): Observable<any> {

		let subject = new Subject<any>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.discResultsService.getDevice(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, "monitor-network", deviceGuids, false).subscribe(
				(result: GetDeviceResultDto) => {
					this.deviceCache = {};
					result.Devices.ForEach((value: Device) => {
						let deviceGuidStr = ValueEncoder.EncodeGuid(value.GUID);
						this.deviceCache[deviceGuidStr] = value;
					});

					subject.next(true);
				},
				(error) => {
					subject.error(false);
				});

		return subject.asObservable();
	}

	private getMonitorState(policy: MonitorPolicy): Observable<any> {

		this.monEditorState.copyChanges.emit(true);

		let subject = new Subject<any>();

		let sessionGuid = this.identityService.sessionGuid;
		let netViewGuid = this.navService.location.netViewGuid;
		let agentGuid = this.navService.location.agentGuid;
		let schedDiscGuid = this.navService.location.schedDiscGuid;
		let schedDiscRsltGuid = this.navService.location.schedDiscRsltGuid;

		this.monService.getMonitorState(sessionGuid, netViewGuid, agentGuid, schedDiscGuid,
			schedDiscRsltGuid, policy, false).subscribe(
				(result: GetMonitorStateResultDto) => {
					this.stateCache = {};
					result.entries.forEach((entry: MonitorStateDto) => {
						let key = this.stateKey(entry.monitorSettingsEntryGuid, entry.deviceGuid);
						this.stateCache[key] = entry;
					});

					subject.next(true);
				},
				(error) => {
					subject.error(false);
				});

		return subject.asObservable();
	}

	private stateKey(entryGuid: string | Guid, deviceGuid: string | Guid): string {

		return (ObjUtils.GuidToString(entryGuid) + ":" + ObjUtils.GuidToString(deviceGuid));
	}

	public BuildForm(): void {
		let protocolSettings = "";

		let useHostnames = false;
		let useAllIpAddresses = true;
		if (this.editor) {
			let monPolicy: MonitorPolicy = this.editor.Policy;
			let monSettings: MonitorSettings_Config = <MonitorSettings_Config>monPolicy.Settings;

			let tmpProtoSettings = this.FindProtocolSettingsByNameType(monSettings.DefaultProtocolName, monSettings.DefaultProtocolType);
			if (tmpProtoSettings) {
				protocolSettings = tmpProtoSettings.GUID;
            }
			useHostnames = monSettings.PollByHostname;
			useAllIpAddresses = monSettings.UseAllIpAddresses;
		}

		this.DeviceCredentials = new UntypedFormControl(protocolSettings);
		this.UseHostnames = new UntypedFormControl(useHostnames);
		this.UseAllIpAddresses = new UntypedFormControl(useAllIpAddresses);

		this.SettingsForm = this.fb.group({
			DeviceCredentials: this.DeviceCredentials,
			UseHostnames: this.UseHostnames,
			UseAllIpAddresses: this.UseAllIpAddresses,
		});
	}

	public applyTempChanges(): void {
		if (!!this.editor && this.editor.HasPolicy) {

			let monPolicy: MonitorPolicy = this.editor.Policy;
			let monSettings: MonitorSettings_Config = <MonitorSettings_Config>monPolicy.Settings;

			monSettings.DefaultProtocolName = "";
			monSettings.DefaultProtocolType = "";

			if (this.DeviceCredentials.value) {
				let guid = this.DeviceCredentials.value;
				if (guid) {
					let protocolSettings = this.FindProtocolSettingsByGuid(guid);
					if (protocolSettings) {
						monSettings.DefaultProtocolName = protocolSettings.Name;
						monSettings.DefaultProtocolType = protocolSettings.SettingsType;
					}
				}
            }
			monSettings.PollByHostname = this.UseHostnames.value;
			monSettings.UseAllIpAddresses = this.UseAllIpAddresses.value;

			this.editor.HasChanges = true;

			this.BuildForm();
		}
	}

	public get invalidTempChanges(): string[] {
		return ["Devices"];
	}

	public addDevice(): void {
		this.deviceSelectorVisible = true;
	}

	private getAllEntriesForDevice(deviceGuid: string): ConfigMonitorListEntry[] {

		let deviceEntries: ConfigMonitorListEntry[] = [];

		this.entries.forEach((entry: ConfigMonitorListEntry) => {
			if (entry.deviceGuid === deviceGuid) {
				deviceEntries.push(entry);
			}
		});

		return deviceEntries;
	}

	public deleteDevice(entry: ConfigMonitorListEntry): void {
		this.deleteDeviceByGuid(entry.deviceGuid);
		this.rebuildEntries();
	}

	private deleteDeviceByGuid(deviceGuid: string): void {
		let deviceEntries: ConfigMonitorListEntry[] = this.getAllEntriesForDevice(deviceGuid);

		deviceEntries.forEach((devEntry: ConfigMonitorListEntry) => {
			let b = this.editor.Remove_ConfigEntry(devEntry.deviceTag, devEntry.settingsEntryTag);
			console.assert(b);
		});
	}

	public syncConfigEntries(): void {

		let monPolicy: MonitorPolicy = this.editor.Policy;
		let monSettings: MonitorSettings_Config = (this.editor.Policy.Settings as MonitorSettings_Config);

		let devices = new Set<Device>();
		monPolicy.DeviceGuids.ForEach((deviceGuid: Guid) => {
			let deviceGuidStr = deviceGuid.ToString();
			if (this.deviceCache[deviceGuidStr]) {
				let device = <Device>this.deviceCache[deviceGuidStr];
				devices.add(device);
			}
			else {
				console.error("Could not find device in cache: " + deviceGuidStr);
			}
		});

		var policyConfigKeys = monSettings.ConfigKeys;

		devices.forEach((device: Device) => {
			let deviceConfigSettings: MonitorSettings_Config = this.editor.GetMonitorSettings_Config(device);
			console.assert(!!deviceConfigSettings);
			if (deviceConfigSettings) {
				console.assert(deviceConfigSettings.GetStartupConfig === deviceConfigSettings.Contains_Name(CommandScriptKeys.StartupConfig));
				console.assert(deviceConfigSettings.GetRunningConfig === deviceConfigSettings.Contains_Name(CommandScriptKeys.RunningConfig));

				// Sync monitor settings
				deviceConfigSettings.CopySettings(monSettings);

				var deviceConfigKeys = deviceConfigSettings.ConfigKeys;

				// Sync monitor entries
				let startupConfigEntry: MonitorSettings_ConfigEntry = deviceConfigSettings.Find_Name(CommandScriptKeys.StartupConfig);
				if (deviceConfigSettings.GetStartupConfig) {
					if (!startupConfigEntry) {
						deviceConfigSettings.Add_Name(CommandScriptKeys.StartupConfig);
					}
				}
				else {
					if (startupConfigEntry) {
						deviceConfigSettings.Remove(startupConfigEntry);
					}
				}

				let runningConfigEntry: MonitorSettings_ConfigEntry = deviceConfigSettings.Find_Name(CommandScriptKeys.RunningConfig);
				if (deviceConfigSettings.GetRunningConfig) {
					if (!runningConfigEntry) {
						deviceConfigSettings.Add_Name(CommandScriptKeys.RunningConfig);
					}
				}
				else {
					if (runningConfigEntry) {
						deviceConfigSettings.Remove(runningConfigEntry);
					}
				}

				let syslogEntry: MonitorSettings_ConfigEntry = deviceConfigSettings.Find_Name(CommandScriptKeys.Syslog);
				if (deviceConfigSettings.GetSyslog) {
					if (!syslogEntry) {
						deviceConfigSettings.Add_Name(CommandScriptKeys.Syslog);
					}
				}
				else {
					if (syslogEntry) {
						deviceConfigSettings.Remove(syslogEntry);
					}
				}

				deviceConfigSettings.Remove_Names(false);

				for (let i = 0; i < deviceConfigKeys.Count; i++) {
					let tmpKey = deviceConfigKeys.Get(i);

					if (tmpKey == CommandScriptKeys.RunningConfig)
						continue;
					if (tmpKey == CommandScriptKeys.StartupConfig)
						continue;

					let customConfigEntry: MonitorSettings_ConfigEntry = deviceConfigSettings.Find_Name(tmpKey);
					if (policyConfigKeys.Contains(tmpKey)) {
						if (!customConfigEntry) {
							deviceConfigSettings.Add_Name(tmpKey);
						}
					}
					else {
						if (customConfigEntry) {
							deviceConfigSettings.Remove(customConfigEntry);
						}
					}
				}

				console.assert(deviceConfigSettings.GetStartupConfig === deviceConfigSettings.Contains_Name(CommandScriptKeys.StartupConfig));
				console.assert(deviceConfigSettings.GetRunningConfig === deviceConfigSettings.Contains_Name(CommandScriptKeys.RunningConfig));
			}
		});

		this.rebuildEntries();
		this.refreshEntries();
	}

	public refreshEntries(): void {
		this.retrieveDevicesAndMonitorStates();
	}

	public onDeviceSelectorOk(result: MonitorDeviceSelectorResult): void {
		let devicesToAdd = new List<string>();
		result.devices.forEach((value: DisplayDeviceListEntryDto) => {
			let deviceGuid = Guid.FromString(value.deviceGuid);
			if (!this.editor.Policy.DeviceGuids.Contains(deviceGuid)) {
				devicesToAdd.Add(value.deviceGuid);
			}
		});

		if (devicesToAdd.Count > 0) {
			this.addDevices(devicesToAdd);
		}
		else {
			this.messageService.displayInfoMessage("No new devices were added");
		}
	}

	public onDeviceSelectorCancel(): void {
		return;
	}

	private addDevices(devicesToAdd: List<string>): void {
		let configMonitorSettings: MonitorSettings_Config = (this.editor.Policy.Settings as MonitorSettings_Config);
		if (configMonitorSettings.GetStartupConfig || configMonitorSettings.GetRunningConfig || configMonitorSettings.GetSyslog || configMonitorSettings.RunCustomScript) {
			this.getDevices(devicesToAdd).subscribe(
				() => {
					let addedDeviceCount = 0;

					devicesToAdd.ForEach((deviceGuidStr: string) => {
						let device: Device = this.deviceCache[deviceGuidStr];
						if (device) {
							if (this.editor.AddConfigMonitor(device)) {
								++addedDeviceCount;
							}
						}
					});

					if (addedDeviceCount > 0) {
						// Make sure new monitor entries have GUIDs
						this.editor.Policy.VerifyGuids();

						this.getMonitorState(this.editor.Policy).subscribe(
							(gmsResult: any) => {
								this.rebuildEntries();
							},
							(gmsError) => {
								this.rebuildEntries();
								this.messageService.displayErrorMessage("Could not retrieve monitor state info");
							});
					}

					if (addedDeviceCount < devicesToAdd.Count) {
						this.messageService.displayErrorMessage("Error adding device(s)");
					}
				},
				() => {
					this.messageService.displayErrorMessage("Could not retrieve monitor device info");
				});
		}
	}

	public doCustomSort(event): void {
		let comparer = function (a: ConfigMonitorListEntry, b: ConfigMonitorListEntry): number {
			let result = 0;

			switch (event.field) {
				case "ipAddress":
					if (a.ipAddressTag && b.ipAddressTag) {
						result = a.ipAddressTag.CompareTo(b.ipAddressTag);
					}
					else if (a.ipAddressTag) {
						result = 1;
					}
					else if (b.ipAddressTag) {
						result = -1;
					}
					break;
				default:
					// Compare string fields
					result = a[event.field].toString().localeCompare(b[event.field].toString());
					break;
			}

			return result * event.order;
		}

		this.entries.sort(comparer);
	}

	private getCurrentNetwork(): void {
		this.Network = null;

		if (this.navService.location.hasNetViewGuid) {
			let networkGuid = this.navService.location.netViewGuid;
			this.Network = this.netFacadeService.findNetwork(networkGuid);
		}
	}

	private FindProtocolSettingsByGuid(guid: string): ProtocolSettings {
		guid = guid.toLowerCase();
		for (let i = 0; i < this._ProtocolSettings.length; ++i) {
			let settings = this._ProtocolSettings[i];
			if (settings.GUID == guid) {
				return settings;
			}
		}
		return null;
	}

	private FindProtocolSettingsByNameType(protocolName: string, protocolType: string): ProtocolSettings {
		for (let i = 0; i < this._ProtocolSettings.length; ++i) {
			let settings = this._ProtocolSettings[i];
			if ((settings.Name == protocolName) && (settings.SettingsType == protocolType)) {
				return settings;
			}
		}
		return null;
	}

	public get ProtocolSettings(): ProtocolSettings[] {
		if (!this._ProtocolSettings && this.Network) {
			this._ProtocolSettings = [];

			let emptyPS = new SshSettings();
			emptyPS.Name = "";
			emptyPS.GUID = "";
			this._ProtocolSettings.push(<ProtocolSettings>emptyPS);

			this.Network.ProtocolSettings.ForEach((ps: ProtocolSettings) => {
				if (ps.SettingsType == ProtocolSettingsTypes.Ssh) {
					this._ProtocolSettings.push(<ProtocolSettings>ps);
				}
				if (ps.SettingsType == ProtocolSettingsTypes.Telnet) {
					this._ProtocolSettings.push(<ProtocolSettings>ps);
				}
				if (ps.SettingsType == ProtocolSettingsTypes.HTTP) {
					this._ProtocolSettings.push(<ProtocolSettings>ps);
				}
			}, this);
		}
		return this._ProtocolSettings;
	}

}
